import { useAuth0 } from '@auth0/auth0-react';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { mobileRoutes, publicRoutes, routes } from './routing/Routes';
import jwt_decode from 'jwt-decode';
import Shell from './Components/Shell/Shell';
import { IRoute } from './routing/types';
import { useAppDispatch, setToken } from './store';
import theme from './Theme';
import { LoadingIndicator, NotAuthorizedError, userHasPermissions, UserPermissionContext } from './Components/CoreLib/library';
import MobileShell from './Components/MobileShell/MobileShell';
import { clarity } from 'clarity-js';
import { usePostHog } from 'posthog-js/react'


function App() {
    const { isAuthenticated, getAccessTokenSilently, isLoading, error, user } = useAuth0();
    const dispatch = useAppDispatch();
    const [accessTokenSet, setAccessTokenSet] = useState<boolean>(false);
    const [userPermissions, setUserPermissions] = useState<string[] | null>(null);
    const posthog = usePostHog()

    useEffect(() => {
        (async () => {
            if (isAuthenticated) {
                if (!userPermissions) {
                    const accessToken = await getAccessTokenSilently({
                        authorizationParams: {
                            redirect_uri: window.location.origin,
                            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                            scope: "SCOPE profile email offline_access",
                        },
                    });
                    const decodedToken = jwt_decode(accessToken);
                    const permissions = ((decodedToken as any)?.permissions as string[]) ?? [];

                    setUserPermissions(permissions);
                    dispatch(setToken(accessToken));
                    setAccessTokenSet(true);
                }
            }
        })();
    }, [userPermissions, setUserPermissions, dispatch, isAuthenticated, getAccessTokenSilently]);

    useEffect(() => {
        let clarityProjectId = process.env.REACT_APP_CLARITY_PROJECT_ID;
        if (isAuthenticated && user?.sub && clarityProjectId) {
            clarity.consent();

            clarity.identify(user.sub)
            
            clarity.start({
                projectId: clarityProjectId,
                upload: 'https://m.clarity.ms/collect',
                track: true,
                content: true,
            });
            
            return () => {
                clarity.stop();
            };
        }
    }, [isAuthenticated, user?.sub]);

    useEffect(() => {
        if (user) {
            posthog?.identify(user.sub, {
                email: user.email,
                name: user.name,
            })
        }
    }, [posthog, user])


    const renderRoute = useCallback((route: IRoute) => {
        if (route.requiredPermissions === undefined || route.requiredPermissions.length === 0) {
            return <Route key={route.path} path={route.path} element={route.component} />;
        }
        if (userPermissions && userHasPermissions(route.requiredPermissions ?? [], userPermissions, route.permissionLogic)) {
            return <Route key={route.path} path={route.path} element={route.component} />;
        } else {
            return <Route key={route.path} path={route.path} element={<NotAuthorizedError />} />;
        }
    }, [userPermissions]);

    const togglePermission = useCallback((permission: string): void => {
        if (!userPermissions) {
            return;
        }
        const isPermissionActive = userPermissions.includes(permission);
        if (isPermissionActive) {
            setUserPermissions([...userPermissions.filter(p => p !== permission)]);
        } else {
            setUserPermissions([...userPermissions, permission]);
        }
    }, [userPermissions])

    const renderedRoutes = useMemo(() => {
        if (isAuthenticated && userPermissions) {
            if (userPermissions.includes("is:hauler")) {
                return (
                    <Route path='/' element={<MobileShell isProtected />}>
                        {mobileRoutes.map(renderRoute)}
                        <Route path="*" element={<Navigate to="/home" />} />
                    </Route>
                );
            } else {
                return (
                    <Route path='/' element={<Shell isProtected />}>
                        {routes.map(renderRoute)}
                        <Route path="*" element={<Navigate to="/home" />} />
                    </Route>
                );
            }
        } else {
            return (
                <>
                    <Route path='/' element={<Shell />}>
                        {publicRoutes.map(renderRoute)}
                        <Route path="*" element={<Navigate to="/home" />} />
                    </Route>
                    <Route path='/' element={<Shell isProtected />}>
                        {routes.map(renderRoute)}
                        <Route path="*" element={<Navigate to="/home" />} />
                    </Route>
                </>
            );
        }
    }, [isAuthenticated, userPermissions, renderRoute]);

    if (isLoading || (isAuthenticated && !accessTokenSet)) {
        return <LoadingIndicator />;
    }

    if (error) {
        if (error.message === "Invalid state") {
            window.location.href = '/';
            return <p />;
        }
        return <p>{error.message}</p>;
    }

    return (
        <BrowserRouter>
            <ThemeProvider theme={theme}>
                <CssBaseline>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <SnackbarProvider maxSnack={3}>
                            <UserPermissionContext.Provider value={{
                                permissions: userPermissions ?? [],
                                togglePermission: togglePermission,
                                clearPermissions: () => setUserPermissions(null)
                            }}>
                                <Routes>
                                    {renderedRoutes}
                                </Routes>
                            </UserPermissionContext.Provider>
                        </SnackbarProvider>
                    </LocalizationProvider>
                </CssBaseline>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
